import { AlertTriangle } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  SegmentedControl,
} from '@/shared/components';
import { ParkingBookingPicker } from './ParkingBookingPicker';
import { useParkingPeriodDraft, type ParkingPeriodMode } from './useParkingPeriodDraft';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyGroupId: string;
  /** Heute in der Zeitzone des Objekts; Vorgabe für Beginn und Ende. */
  today: string;
  /** Die im Raster angehakten Stellplätze — der Dialog fragt nicht noch einmal danach. */
  spotIds: string[];
  spotLabels: string[];
}

const MODES = [
  { value: 'block', label: 'Sperren' },
  { value: 'guest', label: 'Für einen Gast' },
] as const;

const HINTS: Record<ParkingPeriodMode, string> = {
  block:
    'Für Handwerker, Anlieferungen oder Eigenbedarf. Kostenlos, ohne Rechnung und ohne Nachricht.',
  guest:
    'Der Platz wird für die gewählte Buchung gehalten und erscheint im Portal des Gastes, wo er ihn bezahlen kann. Eine Zahlungsaufforderung verschickt StayOn dabei noch nicht.',
};

/**
 * Belegung anlegen — für alle angehakten Stellplätze auf einmal. Das Ende ist **exklusiv**, wie
 * überall im Parkplatz-Modell: bis Samstag gibt den Samstag wieder frei.
 */
export function ParkingPeriodDialog({
  open,
  onOpenChange,
  propertyGroupId,
  today,
  spotIds,
  spotLabels,
}: Props) {
  const { draft, patch, create, invalid, rejected, problem, clearRejected } = useParkingPeriodDraft(
    propertyGroupId,
    spotIds,
    today,
    () => onOpenChange(false),
  );

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Der Knopf ist während des Absendens gesperrt, die Eingabetaste in einem Datumsfeld ist es
    // nicht — ohne diese Prüfung überschreibt die zweite Antwort den Bericht der ersten.
    if (invalid || create.isPending) return;
    clearRejected();
    create.mutate();
  };

  /** Der Server kennt bei einem unbekannten Platz nur die Id — die Beschriftung steht hier. */
  const labelFor = (spotId: string, fallback: string) =>
    spotLabels[spotIds.indexOf(spotId)] ?? fallback;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Belegung anlegen</DialogTitle>
            <DialogDescription>
              {spotLabels.length === 1
                ? `Für ${spotLabels[0]}.`
                : `Für ${spotLabels.length} Stellplätze: ${spotLabels.join(', ')}.`}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <SegmentedControl
              value={draft.mode}
              options={MODES}
              onChange={(mode) => {
                clearRejected();
                patch({ mode, bookingId: '', search: '', note: '' });
              }}
            />
            <p className="text-ink-soft text-sm">{HINTS[draft.mode]}</p>

            <div className="grid gap-3 md:grid-cols-3">
              <FormField label="Von" required>
                <Input
                  type="date"
                  value={draft.from}
                  onChange={(event) => patch({ from: event.target.value, bookingId: '' })}
                />
              </FormField>
              <FormField
                label="Bis"
                required
                hint="Der letzte belegte Tag ist der Vortag — wie bei einer Abreise."
                error={draft.from >= draft.to ? 'Das Ende muss nach dem Beginn liegen.' : undefined}
              >
                <Input
                  type="date"
                  value={draft.to}
                  onChange={(event) => patch({ to: event.target.value, bookingId: '' })}
                />
              </FormField>
              <FormField label="Kennzeichen">
                <Input
                  value={draft.licensePlate}
                  maxLength={15}
                  placeholder="R-KH 3592"
                  onChange={(event) => patch({ licensePlate: event.target.value })}
                />
              </FormField>
            </div>

            {draft.mode === 'block' ? (
              <FormField label="Notiz">
                <Input
                  value={draft.note}
                  maxLength={200}
                  placeholder="Handwerker Meier"
                  onChange={(event) => patch({ note: event.target.value })}
                />
              </FormField>
            ) : (
              <ParkingBookingPicker
                propertyGroupId={propertyGroupId}
                from={draft.from}
                to={draft.to}
                search={draft.search}
                onSearchChange={(search) => patch({ search })}
                value={draft.bookingId}
                onChange={(bookingId) => patch({ bookingId })}
              />
            )}

            {problem && (
              <p role="alert" className="text-sealwax text-sm">
                {problem}
              </p>
            )}
            {rejected.length > 0 && (
              <div className="border-amber/40 bg-amber/8 rounded-md border p-3">
                <div className="text-ink flex items-center gap-2 text-sm font-semibold">
                  <AlertTriangle size={14} strokeWidth={2} className="text-amber" />
                  Nicht für alle Plätze möglich
                </div>
                <ul className="text-ink-soft mt-2 space-y-1 text-[13px]">
                  {rejected.map((row) => (
                    <li key={row.spotId}>
                      <span className="text-ink font-medium">
                        {labelFor(row.spotId, row.spotLabel)}
                      </span>{' '}
                      — {row.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
              Schließen
            </Button>
            <Button type="submit" variant="brand" loading={create.isPending} disabled={invalid}>
              {draft.mode === 'block' ? 'Sperren' : 'Zuordnen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
